import { memo, useCallback, useMemo, useState } from 'react';
import { APPROVAL_STATES } from '../../../../CONSTANTS';
import { ApprovalState } from '../../../../../../../../api/generated/data-contracts';
import {
    DefaultButton,
    Dialog,
    DialogFooter,
    Dropdown,
    IDropdownOption,
    IDropdownProps,
    IRenderFunction,
    Icon,
    PrimaryButton,
    Stack,
    Text,
    mergeStyleSets,
    useTheme,
    getTheme,
    TooltipHost,
} from '@fluentui/react';
import { ApiCalls } from '../../../../../../../../api/api';
import { useMutation } from '@tanstack/react-query';
import { useRowMetaContext } from '../../../../context/RowMetaContext';
import { TimeEntryApprovalDtoState, useApprovalState, useMissingApprovalCount } from '../../../../../../../../_grid/grid3';
import { getApprovalStateBackground } from '../../../../../../../../_helpers/getApprovalStateBackground';
import { useTranslation } from 'react-i18next';
import { useConfigValues } from '../../../../../../../../hooks/queries/useConfigValues';

const stateOptions = APPROVAL_STATES.map((state): IDropdownOption<ApprovalState> => {
    return {
        key: state,
        text: state,
        data: state,
    };
});

export const StateDropdown = memo(function StateDropdown() {
    const {
        data: {
            configuration: { lockProcessedTimeEntries },
        },
    } = useConfigValues();
    const { t } = useTranslation('timesheet');
    const { row, parentRows } = useRowMetaContext<TimeEntryApprovalDtoState>();
    const [approval, setApproval] = useApprovalState(row.id);
    const { mutate: mutateApproval } = useMutation((selectedApprovalState: ApprovalState) =>
        ApiCalls.setApprovalState({ selectedApprovalState, timeEntryId: row.timeEntry.id }),
    );
    const missingApprovalCount = useMissingApprovalCount(row.id);
    const [showProjectClosureWarning, setShowProjectClosureWarning] = useState(false);

    const onChange = useCallback(
        (event: any, option?: IDropdownOption<ApprovalState>) => {
            if (option?.data && option.data !== approval) {
                if (option.data === 'Approved' && missingApprovalCount === 1) {
                    // Warn about project closure
                    setShowProjectClosureWarning(true);
                } else {
                    setApproval(option.data);
                    mutateApproval(option.data);
                }
            }
        },
        [approval, missingApprovalCount, mutateApproval, setApproval],
    );

    const theme = useTheme();

    const approvalBackground = useMemo((): IDropdownProps['styles'] => {
        return {
            title: {
                backgroundColor: getApprovalStateBackground(approval, theme),
            },
        };
    }, [approval, theme]);

    const onRenderCaretDown: IRenderFunction<IDropdownProps> = useCallback(
        (props, defaultRender) => {
            if (missingApprovalCount === 0) {
                return null;
            }
            return defaultRender(props);
        },
        [missingApprovalCount],
    );

    const externallyApprovedMessage = useMemo(() => {
        if (row.externalId && lockProcessedTimeEntries) {
            return t('readOnlyMessages.externallyLocked');
        }
    }, [lockProcessedTimeEntries, row.externalId, t]);

    return (
        <>
            <TooltipHost content={externallyApprovedMessage}>
                <Dropdown
                    //
                    disabled={Boolean(externallyApprovedMessage) || missingApprovalCount === 0}
                    options={stateOptions}
                    onChange={onChange}
                    selectedKey={approval}
                    styles={approvalBackground}
                    onRenderCaretDown={onRenderCaretDown}
                />
            </TooltipHost>
            <Dialog minWidth={400} hidden={!showProjectClosureWarning} dialogContentProps={{ title: 'Approve and close time registration?' }}>
                After all time registrations for this month are approved, you won’t be able to change the approvals for the project{' '}
                <strong>"{parentRows[parentRows.length - 1].name}"</strong> unless the person who submitted the timesheet retracts and resubmits.
                <DialogFooter>
                    <DefaultButton
                        onClick={() => {
                            setShowProjectClosureWarning(false);
                        }}
                    >
                        Cancel
                    </DefaultButton>
                    <PrimaryButton
                        onClick={() => {
                            setShowProjectClosureWarning(false);
                            setApproval('Approved');
                            mutateApproval('Approved');
                        }}
                        className={styles.approveButton}
                    >
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
                            <Text className={styles.approveButtonText}>Approve</Text>
                            <Icon iconName="CheckMark" />
                        </Stack>
                    </PrimaryButton>
                </DialogFooter>
            </Dialog>
        </>
    );
});

const theme = getTheme();
const styles = mergeStyleSets({
    approveButton: {
        backgroundColor: theme.semanticColors.successIcon,
        selectors: {
            ':hover': { backgroundColor: 'rgb(16,100,16)' },
        },
    },
    approveButtonText: { color: theme.semanticColors.accentButtonText },
});
